import moment from "moment";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {
  approveOnboardingTask,
  approvePzValidationRequest,
  getPzValidationRequestById,
  rejectOnboardingTask,
  rejectPzValidationRequest,
} from "../../actions/pzValidationRequest";
import Button from "../../common/button/button";
import mapboxgl from "../../common/mapbox";
import {newModalStyle} from "../../reusable/styles";
import EntityDetail from "./entityDetail";
import RejectTaskModal from "./rejectTaskModal";

const PZ_MARKER_COLOR = "#CC0605";
const ZPO_MARKER_COLOR = "#005a92";

// Groups a Swiss IBAN for display as "CHxx xxxx xxxx xxxx xxxx x" — purely presentational,
// the underlying value stays whatever the backend sent.
const formatIban = (iban) => {
  if (!iban) return iban;
  return iban.replace(/\s/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();
};

// Mapbox GL comes from the shared src/common/mapbox.js module (npm mapbox-gl), same as report/MapWidget.js
const STREETS_STYLE = "mapbox://styles/mapbox/standard";
const SATELLITE_STYLE = "mapbox://styles/mapbox/standard-satellite";
const LINE_ID = "pz-zpo-line";

/**
 * Read-only Mapbox map for the PZ validation request modal.
 * Shows the PZ marker (red) and the ZPO geotag marker (blue), with a Map/Satellite toggle.
 * When both coordinates are the same, it renders one combined marker rather than two
 * indistinguishable pins.
 * Imperative mapbox-gl is used (mirrors src/containers/report/MapWidget.js) because the
 * library is not React-aware.
 */
const PzMap = ({ center, pzMarker, zpoMarker }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const lineCoordsRef = useRef(null); // [[lng,lat],[lng,lat]] when both markers exist, else null
  // True once a style has loaded enough to accept addSource/addLayer. We track this ourselves
  // instead of calling map.isStyleLoaded(): that also waits on sources/tiles and returns false
  // inside the style.load handler fired by setStyle, which made the line vanish on Map/Satellite
  // toggle (it never got re-added). style.load is the right signal; isStyleLoaded() is too strict.
  const styleReadyRef = useRef(false);
  const activeStyleRef = useRef(STREETS_STYLE); // last style we actually pushed via setStyle
  const [mapStyle, setMapStyle] = useState(STREETS_STYLE);

  // Draw/update/remove the thin connector line between the two markers.
  // Lives as a map layer (unlike the DOM markers) so it must be re-added after setStyle.
  // Reads only refs, so a single handler registered at init stays correct across re-renders.
  const renderLine = () => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const coords = lineCoordsRef.current;
    const source = map.getSource(LINE_ID);
    if (!coords) {
      if (map.getLayer(LINE_ID)) map.removeLayer(LINE_ID);
      if (source) map.removeSource(LINE_ID);
      return;
    }
    // Dark thin line reads well on the Map style; satellite imagery needs a lighter, thicker line.
    const isSatellite = activeStyleRef.current === SATELLITE_STYLE;
    const lineColor = isSatellite ? "#fff" : "#444";
    const lineWidth = 2.5;
    const data = { type: "Feature", geometry: { type: "LineString", coordinates: coords } };
    if (source) {
      source.setData(data);
      map.setPaintProperty(LINE_ID, "line-color", lineColor);
      map.setPaintProperty(LINE_ID, "line-width", lineWidth);
    } else {
      map.addSource(LINE_ID, { type: "geojson", data });
      map.addLayer({
        id: LINE_ID,
        type: "line",
        source: LINE_ID,
        slot: "middle",
        layout: { "line-cap": "round", "line-join": "round" },
        // line-occlusion-opacity: the segment hidden behind 3D buildings still shows,
        // ghosted at 40% — fully opaque where unobstructed, faint where it passes behind buildings.
        paint: { "line-color": lineColor, "line-width": lineWidth, "line-dasharray": [2, 2], "line-occlusion-opacity": 0.4 },
      });
    }
  };

  // Init the map once on mount; clean up on unmount (modal close).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STREETS_STYLE,
      center: [center.lng, center.lat],
      zoom: 16,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    // react-responsive-modal mounts the container before it has its final size — resize once laid out.
    map.on("load", () => map.resize());
    // style.load fires on the initial load AND after every setStyle (Map/Satellite toggle), which
    // wipes layers — so this is the single place that (re)draws the connector line.
    map.on("style.load", () => {
      styleReadyRef.current = true;
      // Standard-style config: keep 3D buildings + POIs ON (operator can correlate the
      // marker with real-world buildings/landmarks), consistent daytime lighting.
      // Guarded so a missing config property on a non-Standard style never throws.
      try {
        map.setConfigProperty("basemap", "lightPreset", "day");
      } catch (e) {
        /* not a Standard style — ignore */
      }
      renderLine();
    });
    const resizeTimer = setTimeout(() => map.resize(), 200);
    mapRef.current = map;

    return () => {
      clearTimeout(resizeTimer);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch base style on toggle. DOM Markers/controls survive setStyle (only layers would be lost).
  // Skip the redundant initial call (constructor already used STREETS_STYLE) and mark the style
  // not-ready until style.load fires again, so renderLine doesn't touch a half-torn-down style.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || activeStyleRef.current === mapStyle) return;
    activeStyleRef.current = mapStyle;
    styleReadyRef.current = false;
    map.setStyle(mapStyle);
  }, [mapStyle]);

  // (Re)place markers and frame them whenever coordinates change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const points = [];
    if (pzMarker) {
      markersRef.current.push(
        new mapboxgl.Marker({ color: PZ_MARKER_COLOR }).setLngLat([pzMarker.lng, pzMarker.lat]).addTo(map)
      );
      points.push([pzMarker.lng, pzMarker.lat]);
    }
    if (zpoMarker) {
      markersRef.current.push(
        new mapboxgl.Marker({ color: ZPO_MARKER_COLOR }).setLngLat([zpoMarker.lng, zpoMarker.lat]).addTo(map)
      );
      points.push([zpoMarker.lng, zpoMarker.lat]);
    }

    lineCoordsRef.current = points.length > 1 ? [points[0], points[1]] : null;
    renderLine();

    if (points.length === 1) {
      map.easeTo({ center: points[0], zoom: 17, duration: 0 });
    } else if (points.length > 1) {
      const bounds = points.reduce(
        (b, p) => b.extend(p),
        new mapboxgl.LngLatBounds(points[0], points[0])
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pzMarker?.lat, pzMarker?.lng, zpoMarker?.lat, zpoMarker?.lng]);

  const toggleBtnStyle = (active) => ({
    padding: "5px 12px",
    border: "none",
    cursor: "pointer",
    fontFamily: "Gilroy-Regular",
    fontSize: 12,
    background: active ? "#005a92" : "#fff",
    color: active ? "#fff" : "#333",
  });

  return (
    <div style={{ position: "relative", height: 280, width: "100%", margin: "12px 0" }}>
      <div ref={containerRef} style={{ height: "100%", width: "100%", borderRadius: 6 }} />
      <div
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          display: "flex",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}
      >
        <button type="button" onClick={() => setMapStyle(STREETS_STYLE)} style={toggleBtnStyle(mapStyle === STREETS_STYLE)}>
          Map
        </button>
        <button type="button" onClick={() => setMapStyle(SATELLITE_STYLE)} style={toggleBtnStyle(mapStyle === SATELLITE_STYLE)}>
          Satellite
        </button>
      </div>
    </div>
  );
};

const TASK_STATUS_STYLE = {
  APPROVED:  { background: "#e6f4ea", color: "#1e7e34",  border: "1px solid #a3d9a5" },
  REJECTED:  { background: "#fdecea", color: "#c0392b",  border: "1px solid #f5c6cb" },
  SUBMITTED: { background: "#fff3cd", color: "#856404",  border: "1px solid #ffc107" },
  PENDING:   { background: "#f0f0f0", color: "#6c757d",  border: "1px solid #dee2e6" },
};

// Badge for the ZPO over-limit indicator — reuses the REJECTED status palette
// (same colors as TASK_STATUS_STYLE.REJECTED above) rather than repainting any row/background.
const ZPO_OVER_LIMIT_BADGE_STYLE = {
  background: "#fdecea",
  color: "#c0392b",
  border: "1px solid #f5c6cb",
};

const PzValidationRequestModal = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [rejectTaskModalOpen, setRejectTaskModalOpen] = useState(false);
  const [pendingRejectTaskType, setPendingRejectTaskType] = useState(null);
  const [zpoAddress, setZpoAddress] = useState(null);
  const [expandedImages, setExpandedImages] = useState({});

  const toggleImage = (taskType) =>
    setExpandedImages((prev) => ({ ...prev, [taskType]: !prev[taskType] }));

  const { pzValidationRequest } = useSelector((state) => state.pzValidationRequests);

  useEffect(() => {
    if (!pzValidationRequest?.zpoGeotagLatitude || !pzValidationRequest?.zpoGeotagLongitude) {
      setZpoAddress(null);
      return;
    }
    const lat = parseFloat(pzValidationRequest.zpoGeotagLatitude);
    const lng = parseFloat(pzValidationRequest.zpoGeotagLongitude);
    // Mapbox reverse geocoding — note lng,lat order; result is in `place_name`
    fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${process.env.REACT_APP_MAPBOX_KEY}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.features?.length > 0) setZpoAddress(data.features[0].place_name);
      })
      .catch(() => setZpoAddress(null));
  }, [pzValidationRequest?.zpoGeotagLatitude, pzValidationRequest?.zpoGeotagLongitude]);

  const onClose = () => props.setModalOpen(false);

  const handleApprove = async () => {
    await dispatch(approvePzValidationRequest(pzValidationRequest?.id));
    onClose();
  };

  const handleReject = async () => {
    await dispatch(rejectPzValidationRequest(pzValidationRequest?.id));
    onClose();
  };

  const handleTaskApprove = async (taskType) => {
    await dispatch(approveOnboardingTask(pzValidationRequest?.id, taskType));
    dispatch(getPzValidationRequestById(pzValidationRequest?.id));
  };

  const openTaskRejectModal = (taskType) => {
    setPendingRejectTaskType(taskType);
    setRejectTaskModalOpen(true);
  };

  const handleTaskRejectConfirm = async (reason) => {
    setRejectTaskModalOpen(false);
    await dispatch(rejectOnboardingTask(pzValidationRequest?.id, pendingRejectTaskType, reason));
    dispatch(getPzValidationRequestById(pzValidationRequest?.id));
  };

  const firstMarker =
    pzValidationRequest?.markers?.length > 0 ? pzValidationRequest.markers[0] : null;

  const zpoLat = pzValidationRequest?.zpoGeotagLatitude
    ? parseFloat(pzValidationRequest.zpoGeotagLatitude)
    : null;
  const zpoLng = pzValidationRequest?.zpoGeotagLongitude
    ? parseFloat(pzValidationRequest.zpoGeotagLongitude)
    : null;

  const zpoMarker = zpoLat !== null && zpoLng !== null ? { lat: zpoLat, lng: zpoLng } : null;
  const markersOverlap =
    firstMarker && zpoMarker &&
    Math.abs(parseFloat(firstMarker.latitude) - zpoMarker.lat) < 0.0000001 &&
    Math.abs(parseFloat(firstMarker.longitude) - zpoMarker.lng) < 0.0000001;

  const mapCenter = firstMarker
    ? { lat: parseFloat(firstMarker.latitude), lng: parseFloat(firstMarker.longitude) }
    : zpoMarker || { lat: 47.3769, lng: 8.5417 };

  // Backend-computed (contract §C of BEP-1974) — null-safe against an older backend that
  // doesn't return these fields yet. Clients never compute this distance themselves anymore.
  // Non-finite/unparseable values (e.g. an unexpected "abc" or "Infinity" string) also map
  // to null so the UI falls back to "—"/no badge/no note instead of rendering "NaN m".
  const toFiniteOrNull = (v) => {
    const n = Number(v);
    return v !== null && v !== undefined && Number.isFinite(n) ? n : null;
  };
  const zpoDistanceMeters = toFiniteOrNull(pzValidationRequest?.zpoDistanceMeters);
  const zpoWarnLimitMeters = toFiniteOrNull(pzValidationRequest?.zpoWarnLimitMeters);
  const zpoSignAccuracy = toFiniteOrNull(pzValidationRequest?.zpoSignAccuracy);
  const isOverZpoLimit =
    zpoDistanceMeters !== null && zpoWarnLimitMeters !== null && zpoDistanceMeters > zpoWarnLimitMeters;

  // Banking is optional during onboarding, so the customer may not have submitted it yet.
  // Flat fields straight off pzValidationRequest (payee/iban/street/streetNumber/zip/city) —
  // one-line change here if the backend ever nests these under a sub-object instead.
  const paymentInfo = pzValidationRequest ?? {};
  const hasPaymentInfo = Boolean(paymentInfo.payee || paymentInfo.iban);
  const paymentAddressLine = [
    [paymentInfo.street, paymentInfo.streetNumber].filter(Boolean).join(" "),
    [paymentInfo.zip, paymentInfo.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const openDocument = (url) => {
    if (url) url.split(",").forEach((u) => window.open(u.trim(), "_blank"));
  };

  const getFileName = (url) => {
    try {
      return decodeURIComponent(new URL(url.trim()).pathname.split("/").pop());
    } catch {
      return url.trim().split("/").pop();
    }
  };

  return (
    <>
      <Modal
        open={props.open}
        closeOnEsc
        onClose={onClose}
        center
        styles={newModalStyle({ width: "900px" })}
      >
        <div className="modal__headerLine" />
        <div className="modal__title">{props.title}</div>

        <div style={{ padding: "0 24px" }}>
          {/* ── Zone info ── */}
          <EntityDetail
            label={t("pzValidationRequestModal.pzNumber")}
            value={pzValidationRequest?.parkingZoneNumber}
          />
          <EntityDetail
            label={t("pzValidationRequestModal.pzName")}
            value={pzValidationRequest?.parkingZoneName}
          />
          <EntityDetail
            label={t("pzValidationRequestModal.address")}
            value={pzValidationRequest?.address}
          />
          <EntityDetail
            label={t("pzValidationRequestModal.dateTimeOfRequest")}
            value={moment(pzValidationRequest?.createdOn).format("DD.MM.YYYY HH:mm")}
          />
          <EntityDetail
            label={t("pzValidationRequestModal.customerName")}
            value={pzValidationRequest?.requestorFullName}
          />
          <EntityDetail
            label={t("pzValidationRequestModal.requestor")}
            value={pzValidationRequest?.requestorEmail}
          />
          {pzValidationRequest?.requestorMailingAddress && (
            <EntityDetail
              label={t("pzValidationRequestModal.mailingAddress")}
              value={pzValidationRequest.requestorMailingAddress}
            />
          )}
          {pzValidationRequest?.requestorLocale && (
            <EntityDetail
              label={t("pzValidationRequestModal.preferredLanguage")}
              value={pzValidationRequest.requestorLocale}
            />
          )}

          {/* ── Map ── */}
          <PzMap
            center={mapCenter}
            pzMarker={
              firstMarker
                ? { lat: parseFloat(firstMarker.latitude), lng: parseFloat(firstMarker.longitude) }
                : null
            }
            zpoMarker={markersOverlap ? null : zpoMarker}
          />

          {/* ── Map legend ── */}
          {(firstMarker || zpoMarker) && (
            <div style={{ display: "flex", gap: 16, margin: "4px 0 8px", fontSize: 12, fontFamily: "Gilroy-Regular", color: "#555" }}>
              {firstMarker && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: PZ_MARKER_COLOR, border: "2px solid white", boxShadow: "0 0 0 1px #ccc" }} />
                  {t(markersOverlap
                    ? "pzValidationRequestModal.legendPzAndZpoSign"
                    : "pzValidationRequestModal.legendPzMarker")}
                </div>
              )}
              {zpoMarker && !markersOverlap && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: ZPO_MARKER_COLOR, border: "2px solid white", boxShadow: "0 0 0 1px #ccc" }} />
                  {t("pzValidationRequestModal.legendZpoSign")}
                </div>
              )}
            </div>
          )}

          {pzValidationRequest?.markerMissing && (
            <div style={{ margin: "12px 0", color: "#805b00", fontFamily: "Gilroy-Regular", fontSize: 14 }}>
              {t("pzValidationRequestModal.markerMissingNote")}
            </div>
          )}

          <EntityDetail
            label={t("pzValidationRequestModal.distance")}
            value={
              zpoDistanceMeters !== null ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span>
                    {Math.round(zpoDistanceMeters)} m
                    {zpoSignAccuracy !== null &&
                      ` (±${Math.round(zpoSignAccuracy)} m GPS)`}
                  </span>
                  {isOverZpoLimit && (
                    <span
                      style={{
                        ...ZPO_OVER_LIMIT_BADGE_STYLE,
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 10,
                        display: "inline-block",
                        fontFamily: "Gilroy-Regular",
                      }}
                    >
                      {t("pzValidationRequestModal.zpoOverLimitBadge")}
                    </span>
                  )}
                </span>
              ) : (
                "—"
              )
            }
          />

          {zpoAddress && (
            <EntityDetail
              label={t("pzValidationRequestModal.zpoGeotagAddress")}
              value={zpoAddress}
            />
          )}

          {/* ── Onboarding tasks (with evidence) ── */}
          {pzValidationRequest?.tasks?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontFamily: "Gilroy-Bold", fontSize: 14, marginBottom: 10, color: "#333" }}>
                {t("pzValidationRequestModal.tasks")}
              </div>
              {[...pzValidationRequest.tasks]
                .sort((a, b) => {
                  const ORDER = ["PZ_ADDRESS", "LEGAL_DOCS", "ZPO_PHOTO", "PROOF_OF_OWNERSHIP", "BANKING"];
                  return ORDER.indexOf(a.type) - ORDER.indexOf(b.type);
                })
                .map((task) => {
                const statusStyle = TASK_STATUS_STYLE[task.status] || TASK_STATUS_STYLE.PENDING;
                const isRejected = task.status === "REJECTED";

                const TASK_DOC_TYPES = {
                  ZPO_PHOTO:          ["ZPO_PHOTO"],
                  PROOF_OF_OWNERSHIP: ["PROOF_OF_OWNERSHIP"],
                  LEGAL_DOCS:         ["POWER_OF_ATTORNEY", "CONTRACT"],
                };
                const relevantTypes = TASK_DOC_TYPES[task.type] || [];
                const taskDocs = (pzValidationRequest.zoneDocuments || []).filter(
                  (d) => relevantTypes.includes(d.type)
                );
                const firstDocUrl = taskDocs.length > 0 ? taskDocs[0].storageKey : null;

                return (
                  <div
                    key={task.type}
                    style={{
                      padding: "12px",
                      marginBottom: 8,
                      borderRadius: 6,
                      border: isRejected ? "1px solid #f5c6cb" : "1px solid #e0e0e0",
                      background: isRejected ? "#fdecea" : "#fafafa",
                    }}
                  >
                    {/* Task header row */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "Gilroy-Bold", fontSize: 13, marginBottom: 4 }}>
                          {t(`pzValidationRequestModal.taskType.${task.type}`, task.type)}
                        </div>
                        <span
                          style={{
                            ...statusStyle,
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 10,
                            display: "inline-block",
                            fontFamily: "Gilroy-Regular",
                          }}
                        >
                          {t(`pzValidationRequestModal.taskStatus.${task.status}`, task.status)}
                        </span>
                        {isRejected && task.rejectionReason && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#c0392b", fontFamily: "Gilroy-Regular" }}>
                            {task.rejectionReason}
                          </div>
                        )}
                        {task.type === "ZPO_PHOTO" && isOverZpoLimit && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#c0392b", fontFamily: "Gilroy-Regular" }}>
                            {t("pzValidationRequestModal.zpoOverLimitNote", {
                              distance: Math.round(zpoDistanceMeters),
                              limit: Math.round(zpoWarnLimitMeters),
                              accuracy:
                                zpoSignAccuracy !== null
                                  ? `±${Math.round(zpoSignAccuracy)} m`
                                  : t("pzValidationRequestModal.zpoAccuracyUnknown"),
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginLeft: 12 }}>
                        {task.status !== "APPROVED" && task.status !== "PENDING" && (
                          <button
                            onClick={() => handleTaskApprove(task.type)}
                            style={{
                              padding: "4px 12px", fontSize: 12, fontFamily: "Gilroy-Regular",
                              background: "#e6f4ea", color: "#1e7e34", border: "1px solid #a3d9a5",
                              borderRadius: 4, cursor: "pointer",
                            }}
                          >
                            {t("pzValidationRequestModal.taskApprove")}
                          </button>
                        )}
                        {task.status !== "REJECTED" && task.status !== "PENDING" && (
                          <button
                            onClick={() => openTaskRejectModal(task.type)}
                            style={{
                              padding: "4px 12px", fontSize: 12, fontFamily: "Gilroy-Regular",
                              background: "#fdecea", color: "#c0392b", border: "1px solid #f5c6cb",
                              borderRadius: 4, cursor: "pointer",
                            }}
                          >
                            {t("pzValidationRequestModal.taskReject")}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Payment info — lives inside the Banking task snippet rather than as a
                        standalone section, since it IS that task's submitted data. Hidden
                        entirely when the customer hasn't submitted banking yet (optional task). */}
                    {task.type === "BANKING" && hasPaymentInfo && (
                      <div style={{ marginTop: 10 }}>
                        <EntityDetail label={t("pzValidationRequestModal.payee")} value={paymentInfo.payee} />
                        <EntityDetail label={t("pzValidationRequestModal.iban")} value={formatIban(paymentInfo.iban)} />
                        <EntityDetail label={t("pzValidationRequestModal.paymentAddress")} value={paymentAddressLine} />
                      </div>
                    )}

                    {/* Evidence */}
                    {taskDocs.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        {taskDocs.map((doc, i) => {
                          const isImg = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(doc.storageKey);
                          const expanded = expandedImages[task.type];
                          return isImg ? (
                            <div key={i}>
                              <div style={{ display: "inline-flex", gap: 6, marginBottom: expanded ? 8 : 0 }}>
                                <button
                                  onClick={() => toggleImage(task.type)}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    padding: "5px 12px", fontSize: 12, fontFamily: "Gilroy-Regular",
                                    background: "#fff", color: "#005a92",
                                    border: "1px solid #005a92", borderRadius: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  🖼 {doc.fileName} {expanded ? "▲" : "▼"}
                                </button>
                                <button
                                  onClick={() => window.open(doc.storageKey, "_blank")}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    padding: "5px 12px", fontSize: 12, fontFamily: "Gilroy-Regular",
                                    background: "#fff", color: "#005a92",
                                    border: "1px solid #005a92", borderRadius: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  ↗
                                </button>
                              </div>
                              {expanded && (
                                <img
                                  src={doc.storageKey}
                                  alt={doc.fileName}
                                  style={{
                                    maxWidth: "100%", borderRadius: 4,
                                    display: "block", cursor: "pointer",
                                  }}
                                  onClick={() => window.open(doc.storageKey, "_blank")}
                                />
                              )}
                            </div>
                          ) : (
                            <button
                              key={i}
                              onClick={() => window.open(doc.storageKey, "_blank")}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "5px 12px", fontSize: 12, fontFamily: "Gilroy-Regular",
                                background: "#fff", color: "#005a92",
                                border: "1px solid #005a92", borderRadius: 4,
                                cursor: "pointer", marginRight: 6, marginBottom: 4,
                              }}
                            >
                              📄 {doc.fileName}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Whole-request buttons ── */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
          <Button
            text={t("pzValidationRequestModal.reject")}
            buttonStyle={{
              marginBottom: "20px",
              marginTop: "20px",
              height: "40px",
              width: "210px",
              backgroundColor: "#CC0605",
              fontFamily: "Gilroy-Regular",
              marginRight: "28px",
            }}
            onClick={handleReject}
          />
          <Button
            text={t("pzValidationRequestModal.approve")}
            buttonStyle={{
              marginBottom: "20px",
              marginTop: "20px",
              height: "40px",
              width: "210px",
              fontFamily: "Gilroy-Regular",
              opacity: pzValidationRequest?.canApprove ? 1 : 0.45,
              cursor: pzValidationRequest?.canApprove ? "pointer" : "not-allowed",
            }}
            onClick={pzValidationRequest?.canApprove ? handleApprove : undefined}
            disabled={!pzValidationRequest?.canApprove}
          />
        </div>
      </Modal>

      <RejectTaskModal
        open={rejectTaskModalOpen}
        onClose={() => setRejectTaskModalOpen(false)}
        onConfirm={handleTaskRejectConfirm}
      />
    </>
  );
};

export default PzValidationRequestModal;
